import { useMemo } from 'react';
import styled from '@emotion/styled';
import {
  breakpoints,
  fontSize,
  Modal,
  palette,
  spacing,
  Button,
} from '@playdapp/ui';

import type { BridgeCheckMetadata } from 'types/bridge';

import Image from '@/components/Image';
import Icon from '@/components/Icon';

type Props = {
  isOpen: boolean;
  handleOpen: (isOpen: boolean) => void;
  handleReceive: () => void;
  items: BridgeCheckMetadata[];
  isAvailable: boolean;
  isClicked: boolean;
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ImageBlock = styled.div<{ isAvailable: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: ${spacing.m} 0;
  margin-bottom: ${spacing.m};
  background-color: ${({ isAvailable }) =>
    isAvailable ? palette.gray300 : palette.white};
  border-radius: 1rem;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const ImageBox = styled.figure`
  display: flex;
  align-items: center;

  margin-bottom: 0;

  ${breakpoints.down('md')} {
    max-height: 100px;
    max-width: 100px;
  }
`;

const ModalTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.s};
  margin-bottom: ${spacing.xxl};

  ${breakpoints.down('md')} {
    margin-bottom: ${spacing.s};
  }
`;

const ModalTitle = styled.h3`
  font-size: ${fontSize.h3};
  font-weight: 900;
  text-align: center;

  ${breakpoints.down('md')} {
    font-size: ${fontSize.h5};
  }
`;

const ModalSubTitle = styled.div`
  font-size: ${fontSize.p3};
  color: ${palette.gray900};
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.s};

  button {
    flex: 1;
    max-width: ${({ theme }) => theme.rem(205)};
    font-weight: 600;
  }

  ${breakpoints.down('md')} {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const BridgeReceivedModal = ({
  isOpen,
  handleOpen,
  handleReceive,
  items,
  isAvailable = false,
  isClicked = false,
}: Props) => {
  const info = useMemo(
    () => [
      {
        title: 'Receive from ETH',
        line1: (count?: number) =>
          `There are [${count}] NFTs transferred from the ETH market.`,
        line2: 'Would you like to receive NFTs?',
      },
      {
        title: 'There is no NFT to receive',
        line1: () =>
          'Make sure that the transaction of the NFT transferred from the ETH market has been completed.',
      },
    ],
    [],
  );

  const toggleClose = (isModalOpen: boolean) => () => {
    handleOpen(isModalOpen);
  };

  return (
    <Modal isOpen={isOpen} handleOpen={toggleClose(false)}>
      <ModalContent>
        <ImageBlock isAvailable={isAvailable}>
          <ImageContainer>
            <ImageBox>
              {isAvailable && items.length > 0 ? (
                <Image
                  src={items[0].image}
                  alt="random-r"
                  width="200px"
                  height="200px"
                />
              ) : (
                <Icon name="close-red" size={72} />
              )}
            </ImageBox>
          </ImageContainer>
        </ImageBlock>
        <ModalTitleBlock>
          <ModalTitle>{isAvailable ? info[0].title : info[1].title}</ModalTitle>
          <ModalSubTitle>
            {isAvailable ? (
              <>
                <p>{info[0].line1(items.length)}</p>
                <p>{info[0].line2}</p>
              </>
            ) : (
              <p>{info[1].line1()}</p>
            )}
          </ModalSubTitle>
        </ModalTitleBlock>
        <ButtonGroup>
          {isAvailable && (
            <Button onClick={handleReceive} isActive={isClicked} size="lg">
              Receive
            </Button>
          )}
          {!isAvailable && (
            <Button onClick={toggleClose(false)} size="lg">
              OK
            </Button>
          )}
        </ButtonGroup>
      </ModalContent>
    </Modal>
  );
};

export default BridgeReceivedModal;
